/**
 * Mock Semantic Integrity Dashboard Data
 * 
 * Sample data derived from the maintenance receipt:
 * - total findings: 25
 * - total concepts: 10
 * - high confidence concepts: 8
 * - medium confidence concepts: 2
 * - reconciliation proposals: 19 pending
 * - recommended reviews: 4
 * 
 * IMPORTANT: This data represents an imported workspace, not the local repo.
 */

import {
  SemanticIntegrityDashboardModel,
  ConceptHealthSummary,
  FindingsSummary,
  ReconciliationProposalSummary,
  DormantSystemSummary,
  DriftFinding,
  DuplicateCandidate,
  StaleLinkFinding,
  RecommendedReview,
  ImportedWorkspace
} from './types';

export const mockConceptHealthSummary: ConceptHealthSummary = {
  totalConcepts: 10,
  highConfidenceConcepts: 8,
  mediumConfidenceConcepts: 2,
  lowConfidenceConcepts: 0,
  conceptsWithDoctrine: 8,
  conceptsWithCloseouts: 5,
  conceptsWithCapabilities: 0,
  conceptsWithMemoryReferences: 10,
  dormantConcepts: 0
};

export const mockFindingsSummary: FindingsSummary = {
  totalFindings: 25,
  findingsByCategory: {
    conceptDrift: 2,
    duplicateCandidates: 2,
    unmappedTerminology: 0,
    staleDoctrineLinks: 0,
    staleCloseoutLinks: 0,
    staleCapabilityLinks: 0,
    staleMemoryLinks: 0,
    lowConfidenceConcepts: 2,
    unresolvedReconciliationProposals: 19,
    dormantConcepts: 0,
    dormantMemorySystems: 0,
    unresolvedGovernanceRecoveryItems: 0
  },
  findingsBySeverity: {
    CRITICAL: 0,
    HIGH: 2,
    MEDIUM: 4,
    LOW: 19
  }
};

export const mockReconciliationSummary: ReconciliationProposalSummary = {
  totalProposals: 19,
  pendingProposals: 19,
  inReviewProposals: 0,
  approvedProposals: 0,
  rejectedProposals: 0,
  resolvedProposals: 0,
  proposalsByCategory: {
    mergeCandidateReview: 2,
    doctrineLinkNeeded: 2,
    closeoutLinkNeeded: 5,
    capabilityLinkNeeded: 10,
    confidenceReviewNeeded: 2,
    noActionRequired: 0
  }
};

export const mockDormantSystemSummary: DormantSystemSummary = {
  dormantConcepts: 0,
  dormantMemorySystems: 0,
  dormantCapabilities: 0,
  totalDormantItems: 0
};

export const mockDriftFindings: DriftFinding[] = [
  {
    findingId: 'concept-drift-1781795600000-0',
    conceptId: 'capability-absorption-v1',
    conceptLabel: 'Capability Absorption',
    severity: 'MEDIUM',
    confidence: 'MEDIUM',
    description: 'Concept has confidence level MEDIUM, which may indicate semantic drift or incomplete authoritative sources',
    recommendedAction: 'Review concept for semantic drift and add authoritative sources to improve confidence',
    humanReviewRequired: true
  },
  {
    findingId: 'concept-drift-1781795600000-1',
    conceptId: 'formation-gate-v1',
    conceptLabel: 'Formation Gate',
    severity: 'MEDIUM',
    confidence: 'MEDIUM',
    description: 'Concept has confidence level MEDIUM, which may indicate semantic drift or incomplete authoritative sources',
    recommendedAction: 'Review concept for semantic drift and add authoritative sources to improve confidence',
    humanReviewRequired: true
  }
];

export const mockDuplicateCandidates: DuplicateCandidate[] = [
  {
    findingId: 'duplicate-candidates-1781795600000-0',
    conceptIds: ['pr-train-governed-merge-v1'],
    conceptLabels: ['PR Train'],
    severity: 'HIGH',
    confidence: 'MEDIUM',
    description: 'Label "merge train" appears in multiple label types within the same concept',
    recommendedAction: 'Review whether these concepts should be merged or if labels should be reassigned',
    humanReviewRequired: true
  },
  {
    findingId: 'duplicate-candidates-1781795600000-1',
    conceptIds: ['admission-boundary-gate-v1'],
    conceptLabels: ['Admission'],
    severity: 'HIGH',
    confidence: 'MEDIUM',
    description: 'Label "governed admission" appears in multiple label types within the same concept',
    recommendedAction: 'Review whether these concepts should be merged or if labels should be reassigned',
    humanReviewRequired: true
  }
];

export const mockStaleLinks: StaleLinkFinding[] = [];

export const mockRecommendedReviews: RecommendedReview[] = [
  {
    reviewId: 'concept-review-1781795600000-0',
    reviewType: 'concept_merge_review',
    priority: 'HIGH',
    affectedConceptIds: ['pr-train-governed-merge-v1'],
    affectedConceptLabels: ['PR Train'],
    rationale: 'Concept has 2 findings requiring review (duplicate candidates, unresolved reconciliation)',
    estimatedEffort: '1-2 hours',
    humanReviewRequired: true
  },
  {
    reviewId: 'concept-review-1781795600000-1',
    reviewType: 'concept_merge_review',
    priority: 'HIGH',
    affectedConceptIds: ['admission-boundary-gate-v1'],
    affectedConceptLabels: ['Admission'],
    rationale: 'Concept has 2 findings requiring review (duplicate candidates, unresolved reconciliation)',
    estimatedEffort: '1-2 hours',
    humanReviewRequired: true
  },
  {
    reviewId: 'concept-review-1781795600000-2',
    reviewType: 'confidence_review',
    priority: 'MEDIUM',
    affectedConceptIds: ['capability-absorption-v1'],
    affectedConceptLabels: ['Capability Absorption'],
    rationale: 'Concept has 2 findings requiring review (concept drift, low confidence)',
    estimatedEffort: '1 hour',
    humanReviewRequired: true
  },
  {
    reviewId: 'concept-review-1781795600000-3',
    reviewType: 'confidence_review',
    priority: 'MEDIUM',
    affectedConceptIds: ['formation-gate-v1'],
    affectedConceptLabels: ['Formation Gate'],
    rationale: 'Concept has 2 findings requiring review (concept drift, low confidence)',
    estimatedEffort: '1 hour',
    humanReviewRequired: true
  }
];

export const mockImportedWorkspace: ImportedWorkspace = {
  workspaceId: 'workspace-sample-customer-001',
  sourceType: 'fixture',
  sourceBoundary: 'fixture-library',
  importedAt: '2026-06-22T00:00:00Z',
  analyzedArtifacts: [
    'concepts/concept-identity-registry-v1.json',
    'semantic-memory/semantic-concept-index-v1.json',
    'reconciliation/workspace-self-reconciliation-proposals-v1.json'
  ],
  excludedArtifacts: [
    '.git',
    'node_modules',
    'build',
    'dist'
  ],
  proofOnly: true,
  executionAuthority: false,
  privacyBoundary: 'public',
  mutationAllowed: false
};

export const mockSemanticIntegrityDashboardModel: SemanticIntegrityDashboardModel = {
  receiptId: 'workspace-self-maintenance-1781795600000',
  generatedAt: '2026-06-22T00:00:00Z',
  scanVersion: 'v1',
  importedWorkspace: mockImportedWorkspace,
  findingsSummary: mockFindingsSummary,
  conceptHealthSummary: mockConceptHealthSummary,
  reconciliationSummary: mockReconciliationSummary,
  dormantSystemSummary: mockDormantSystemSummary,
  driftFindings: mockDriftFindings,
  duplicateCandidates: mockDuplicateCandidates,
  staleLinks: mockStaleLinks,
  recommendedReviews: mockRecommendedReviews,
  boundary: {
    proofOnly: true,
    executionAuthority: false,
    advisoryOnly: true,
    noAutomaticMerge: true,
    noAutomaticRename: true,
    noAutomaticDelete: true,
    noAutomaticAdmission: true,
    noAutomaticCapabilityPromotion: true,
    noAutomaticCloseoutCreation: true,
    noMemoryRewriting: true,
    noHistoricalArtifactRewriting: true,
    humanReviewRequired: true
  },
  metadata: {
    scanDuration: 0.5,
    sourcesScanned: [
      'artifacts/concepts/concept-identity-registry-v1.json',
      'artifacts/semantic-memory/semantic-concept-index-v1.json',
      'artifacts/concepts/workspace-self-reconciliation-proposals-v1.json'
    ],
    scanTrigger: 'manual'
  }
};
